import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import {
  IconCellRenderer,
  LinkCellRenderer,
  confirm,
} from '@trueprodigy/components';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, lifecycle, withHandlers } from 'recompose';
import { Field, reduxForm } from 'redux-form/immutable';
import styled from 'styled-components';

import CustomUploadInput from '@components/CustomUploadInput';
import AgGrid from '@components/AgGrid/AgGrid';
import { fetchingState, RIGHTS } from '@constants';
import * as eventActions from '@modules/events/actions/event';
import {
  eventDataSelector,
  linkedImagesSelector,
  linkedImagesStateSelector,
} from '@modules/events/selectors/event';
import { userDataSelector, userStatusSelector } from '@selectors/global';
import { hasRight } from '@utils';

const buildColumnDefs = ({
  openDocument,
  handleDelete,
  setSelectedAttachment,
  disabled,
}) => [
    { headerName: 'Description', field: 'attachmentDescription', editable: true },
    { headerName: 'Created By', field: 'createdBy' },
    { headerName: 'Created Date', field: 'createDt' },
    {
      headerName: 'Attachment ID',
      field: 'fileAttachmentID',
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: (_, record) => setSelectedAttachment(record),
      },
    },
    {
      headerName: 'File',
      field: 's3ID',
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: (_, record) =>
          openDocument({ fileAttachmentID: record.fileAttachmentID }),
      },
    },
    {
      headerName: 'Actions',
      field: 'fileAttachmentActions',
      width: 120,
      maxWidth: 120,
      minWidth: 120,
      cellRenderer: IconCellRenderer,
      cellRendererParams: {
        icons: [
          {
            icon: !disabled && 'fa-trash',
            onClick: (props, record) =>
              handleDelete({
                fileAttachmentEventID: record.fileAttachmentEventID,
              }),
          },
        ],
      },
    },
  ];

const FileAttachmentsForm = ({
  documentList,
  documentListState,
  handleDelete,
  handleSubmit,
  setSelectedAttachment,
  openDocument,
  onCellValueChanged,
  hasEventRight,
  userStatus
}) => {
  const isReadOnly = userStatus.get('readOnly');

  return (
    <FileAttachmentsForm.Wrapper onSubmit={handleSubmit}>
      <FileAttachmentsForm.UploadSection>
        <Field
          component={props => (
            <CustomUploadInput multiple disabled={isReadOnly || !hasEventRight()} {...props} />
          )}
          allowScan
          name="attachments"
        />
      </FileAttachmentsForm.UploadSection>

      <FileAttachmentsForm.TableWrapper className="ag-theme-material">
        <Typography style={{ fontSize: '24px', fontWeight: '500' }} variant="h4">
          LINKED IMAGE ATTACHMENTS
        </Typography>

        {documentListState === fetchingState.FETCHING ? (
          <div style={{ textAlign: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <AgGrid
            autoResizeToFit
            columnDefs={buildColumnDefs({
              openDocument,
              handleDelete,
              setSelectedAttachment,
              disabled: isReadOnly || !hasEventRight(),
            })}
            disablePreferenceSave
            domLayout="autoHeight"
            name="imageAttachmentsGrid"
            rowData={documentList}
            gridOptions={{
              onCellValueChanged: onCellValueChanged,
            }}
          />
        )}
      </FileAttachmentsForm.TableWrapper>
    </FileAttachmentsForm.Wrapper>
  )
};

FileAttachmentsForm.propTypes = {
  currentEvent: PropTypes.object,
  documentList: PropTypes.array,
  documentListState: PropTypes.string,
  eventID: PropTypes.string,
  handleDelete: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  hasEventRight: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  setSelectedAttachment: PropTypes.func.isRequired,
  openDocument: PropTypes.func.isRequired,
  onCellValueChanged: PropTypes.func.isRequired,
  userStatus: PropTypes.any,
};

FileAttachmentsForm.defaultProps = {
  currentEvent: {},
  documentList: [],
  documentListState: null,
};

FileAttachmentsForm.Wrapper = styled.form`
  background: white;
  display: flex;
  flex-direction: row;
  min-height: 350px;
  padding: 35px;
  border-top: 4px solid #acb8cc;
`;

FileAttachmentsForm.UploadSection = styled.div`
  width: 300px;
`;

FileAttachmentsForm.Header = styled.header`
  margin-bottom: 20px;
`;

FileAttachmentsForm.TableWrapper = styled.div`
  width: calc(100% - 330px);
  margin-left: 30px;

  .ag-menu {
    max-height: 170px;
  }

  .ag-overlay-no-rows-center {
    display: none;
  }
`;

const mapStateToProps = state => ({
  currentEvent: eventDataSelector(state),
  documentList: linkedImagesSelector(state),
  documentListState: linkedImagesStateSelector(state),
  userData: userDataSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  deleteEventLinkedImage: eventActions.deleteEventLinkedImageForEvent,
  fetchEventLinkedImages: eventActions.fetchEventLinkedImages,
  uploadEventImages: eventActions.uploadEventImages,
  attachEventsImages: eventActions.attachEventsImages,
  setEvent: eventActions.setCurrentEvent,
  downloadEventImage: eventActions.downloadEventImage,
  updateEventImage: eventActions.updateEventImageForEvent,
};

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withRouter,
  withHandlers({
    hasEventRight: ({ userData }) => () => {
      return hasRight(userData.get('userrights').toJS(), RIGHTS.EVENTS.EVENT);
    },
    handleSubmit: ({
      uploadEventImages,
      attachEventsImages,
      fetchEventLinkedImages,
      currentEvent,
    }) => async ({ attachments }) => {
      const { eventID } = currentEvent;
      const formData = new FormData();
      attachments.forEach(attachment => {
        formData.append('uploadfiles', attachment);
      });
      const imagesRes = await uploadEventImages({
        payload: formData,
      });
      if (imagesRes && imagesRes.results) {
        await attachEventsImages({
          fileAttachmentIDs: imagesRes.results.map(a => a.fileAttachmentID),
          eventIDs: [eventID],
        });
      }
      fetchEventLinkedImages({
        eventID,
      });
    },
    handleDelete: ({ deleteEventLinkedImage }) => ({
      fileAttachmentEventID,
    }) => {
      return confirm('link').then(() =>
        deleteEventLinkedImage({
          fileAttachmentEventID,
        }),
      );
    },
    openDocument: ({ downloadEventImage }) => ({ fileAttachmentID }) => {
      downloadEventImage({
        fileAttachmentID,
      });
    },
    onCellValueChanged: ({ updateEventImage }) => params => {
      updateEventImage({
        fileAttachmentID: params.data.fileAttachmentID,
        payload: {
          attachmentDescription: params.data.attachmentDescription,
          s3ID: params.data.s3ID,
          lastUpdateDt: params.data.fileAttachmentUpdateDt || '',
        },
      });
    },
  }),
  lifecycle({
    componentDidMount() {
      this.props.fetchEventLinkedImages({ eventID: this.props.eventID });
    },
  }),
  reduxForm({
    form: 'eventImageAttachments',
    onChange: (values, dispatch, props) => {
      props.handleSubmit(values.toJS());
    },
  }),
);

export default enhance(FileAttachmentsForm);
